/**
 * Organization Detail Panel (Phase 30 Wave 4 chunk 30.4.3c)
 *
 * Renders inside the expanded row of `OrganizationsPage` when a super_admin
 * clicks an organization. Three sub-sections:
 *   1. Org metadata header (name, slug, dates, user count).
 *   2. Users sub-section — table + "Add User" button.
 *   3. Toggle clusters — Training Distribution + Living SOPs.
 *
 * The two toggle clusters each have an INDEPENDENT Save button that PATCHes
 * only its own JSONB cluster. The backend (`PATCH /api/admin/orgs/:orgId/settings`)
 * shallow-merges per top-level cluster, so sending only the cluster being
 * edited preserves the other cluster's leaves.
 *
 * Bug D-2 compliance: Living SOPs cluster has ONLY the `enabled` switch.
 * `scopeSectionIds` and `scopeSources` are documented in the service but
 * not read at runtime; surfacing them would create silent no-ops. See
 * `~/.claude/plans/phase-30-wave-3-toggle-audit.md` Bug D-2.
 *
 * Toast fallback: inline green/red banners that auto-dismiss after 4s.
 * No global toast system exists today — verified via grep at chunk start.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  CircleSlash,
  Layers,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  X,
} from 'lucide-react';
import {
  useOrganization,
  useOrgSectionsLookup,
  useOrgUsers,
  useUpdateOrgSettings,
  useUpdateOrgUser,
  type AssignableRole,
  type OrganizationDetail,
  type OrgSectionLookupItem,
  type OrgUser,
} from '../../../hooks/useOrganizations';
import { ApiError } from '../../../lib/api';
import { ScopeChipPicker } from '../../kpi/ScopeChipPicker';
import { AddUserModal } from './AddUserModal';

interface OrganizationDetailPanelProps {
  orgId: string;
}

interface SuccessBanner {
  message: string;
  tone: 'success' | 'info';
}

const formatDate = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Pull the most specific message out of a failed user PATCH.
 *
 * The API preserves hand-written guard text in `error.message` for
 * badRequest / forbidden / notFound (`apps/api/src/middleware/error.ts:76-81`),
 * so Guard A, Guard B, the role-inversion 403 and the two 404s all arrive
 * readable. Zod failures do NOT: they collapse to a flat "Invalid request
 * data" and hide the real text under `error.details.errors[].message`
 * (`error.ts:63-73`). A super_admin debugging someone else's org needs to know
 * WHICH rule fired, so read the details array when it is present.
 *
 * The three Zod branches on this endpoint — role `super_admin`, an empty body,
 * a malformed `:userId` — are all unreachable from this table: the select
 * offers only the four assignable roles, every call sends exactly one field,
 * and the id comes from the server's own list. Unwrapped anyway, because
 * "unreachable" is a property of today's caller, not of the endpoint.
 */
function actionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const issues = (err.details as { errors?: { message?: string }[] } | undefined)
      ?.errors;
    const detail = issues
      ?.map((e) => e.message)
      .filter((m): m is string => !!m)
      .join('; ');
    return detail || err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function OrganizationDetailPanel({ orgId }: OrganizationDetailPanelProps) {
  const {
    data: org,
    isLoading: orgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useOrganization(orgId);

  // Dedicated users query — invalidated on add-user success without
  // forcing a refetch of the toggle state.
  const {
    data: users,
    isLoading: usersLoading,
  } = useOrgUsers(orgId);

  if (orgLoading) {
    return <PanelLoading />;
  }

  if (orgError) {
    return <PanelError onRetry={() => refetchOrg()} />;
  }

  if (!org) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Header org={org} />

      <UsersSection
        orgId={orgId}
        orgName={org.name}
        users={users ?? org.users}
        isLoading={usersLoading}
      />

      <TrainingDistributionCluster
        orgId={orgId}
        initial={{
          enabled: org.trainingDistributionEnabled,
          scopeSectionIds:
            org.settings?.trainingDistribution?.scopeSectionIds ?? [],
        }}
      />

      <LivingSopsCluster
        orgId={orgId}
        initial={{ enabled: org.livingSopsEnabled }}
      />

      <SupervisedModeCluster
        orgId={orgId}
        initial={{
          enabled: org.supervisedModeEnabled,
          // Round-tripped verbatim, never edited here — see the cluster's
          // comment for why there is no picker.
          scopeScheduleIds: org.settings?.supervisedMode?.scopeScheduleIds ?? null,
        }}
      />
    </div>
  );
}

// ============================================
// HEADER — read-only metadata
// ============================================

function Header({ org }: { org: OrganizationDetail }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat label="Slug" value={<code className="text-xs">{org.slug}</code>} />
        <Stat
          label="Auth0 Org ID"
          value={
            org.auth0OrgId ? (
              <code className="text-xs">{org.auth0OrgId}</code>
            ) : (
              <span className="text-gray-400 text-xs italic">none</span>
            )
          }
        />
        <Stat label="Created" value={formatDate(org.createdAt)} />
        <Stat label="Updated" value={formatDateTime(org.updatedAt)} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

// ============================================
// USERS SUB-SECTION
// ============================================

interface UsersSectionProps {
  orgId: string;
  orgName: string;
  users: OrgUser[];
  isLoading: boolean;
}

function UsersSection({ orgId, orgName, users, isLoading }: UsersSectionProps) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [banner, setBanner] = useState<SuccessBanner | null>(null);
  // Row-action failures from the Actions column. Kept separate from `banner`
  // rather than widening `SuccessBanner['tone']`, which is also the prop type
  // of `ToggleClusterCard` below and has no error styling there.
  const [actionError, setActionError] = useState<string | null>(null);

  // Auto-dismiss banner after 4s.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const handleAddSuccess = ({
    email,
    passwordResetSent,
    auth0UserExisted,
  }: {
    email: string;
    passwordResetSent: boolean;
    auth0UserExisted: boolean;
  }) => {
    if (auth0UserExisted) {
      setBanner({
        tone: 'info',
        message: `Recovered orphan Auth0 user — ${email} can now log in.`,
      });
    } else if (passwordResetSent) {
      setBanner({
        tone: 'success',
        message: `User created. Password reset email sent to ${email}.`,
      });
    } else {
      setBanner({
        tone: 'success',
        message: `User created. Password reset email failed — share the reset link manually.`,
      });
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">
            Users
            <span className="ml-2 text-xs font-medium text-gray-500">
              ({users.length})
            </span>
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setIsAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-guild-green px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-guild-green/95 transition-colors"
        >
          <UserPlus size={14} />
          Add User
        </button>
      </div>

      {/* Banner */}
      {banner && (
        <div
          className={`mx-5 mt-4 flex items-start gap-3 rounded-lg border p-3 ${
            banner.tone === 'success'
              ? 'border-emerald-100 bg-emerald-50'
              : 'border-blue-100 bg-blue-50'
          }`}
        >
          <CheckCircle2
            size={16}
            className={
              banner.tone === 'success'
                ? 'mt-0.5 text-emerald-600'
                : 'mt-0.5 text-blue-600'
            }
          />
          <p
            className={`text-xs ${
              banner.tone === 'success' ? 'text-emerald-800' : 'text-blue-800'
            }`}
          >
            {banner.message}
          </p>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            className="ml-auto rounded p-0.5 text-gray-400 hover:bg-white/40 hover:text-gray-600"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Row-action failure. Deliberately NOT auto-dismissed like the success
          banner above: these carry the specific server rule that fired (last
          admin, super_admin target, role inversion), and four seconds is not
          long enough to read one. Cleared when the next action starts, or by
          the X. */}
      {actionError && (
        <div className="mx-5 mt-4 flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-3">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-red-600" />
          <p className="text-xs text-red-800">{actionError}</p>
          <button
            type="button"
            onClick={() => setActionError(null)}
            aria-label="Dismiss"
            className="ml-auto rounded p-0.5 text-gray-400 hover:bg-white/40 hover:text-gray-600"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Body */}
      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-500">
            <Loader2 size={16} className="mr-2 animate-spin" />
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <EmptyUsersState onAdd={() => setIsAddOpen(true)} />
        ) : (
          <UsersTable
            orgId={orgId}
            users={users}
            onActionError={setActionError}
          />
        )}
      </div>

      <AddUserModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        orgId={orgId}
        orgName={orgName}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
}

function EmptyUsersState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-mint/40 mb-3">
        <Users size={20} className="text-guild-green" />
      </div>
      <p className="text-sm font-medium text-gray-700">
        No users in this organization
      </p>
      <p className="mt-1 text-xs text-gray-500 max-w-xs">
        Click "Add User" to onboard the first one.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-guild-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-guild-green/95 transition-colors"
      >
        <UserPlus size={14} />
        Add User
      </button>
    </div>
  );
}

interface UsersTableProps {
  orgId: string;
  users: OrgUser[];
  /** Reports a failed row action up to the section banner. `null` clears it. */
  onActionError: (message: string | null) => void;
}

function UsersTable({ orgId, users, onActionError }: UsersTableProps) {
  // One mutation for the whole table, not one per row: a hook cannot be called
  // inside the .map() below, and disabling every control for the duration of a
  // write also stops two rows racing each other against a single invalidation.
  const updateMutation = useUpdateOrgUser(orgId);
  const isPending = updateMutation.isPending;

  // No optimistic update anywhere in here. The <select> is controlled by the
  // server value, so a rejected change snaps back to what the API still holds
  // instead of stranding the row on a value that was never written.
  const handleRoleChange = async (user: OrgUser, nextRole: AssignableRole) => {
    if (nextRole === user.role) return;
    onActionError(null);
    try {
      await updateMutation.mutateAsync({ userId: user.id, role: nextRole });
    } catch (err) {
      onActionError(
        actionErrorMessage(err, `Failed to change the role for ${user.email}`)
      );
    }
  };

  const handleToggleActive = async (user: OrgUser) => {
    // Deactivation on this surface is DB-side only — the Auth0 account is
    // never blocked, so the user CAN still log in; they are refused 403 on
    // their first API call. The org-scoped Settings → Users page says "they
    // will no longer be able to sign in" (UsersPage.tsx:129) because that path
    // does block Auth0. Copying that wording here would be false.
    const confirmed = window.confirm(
      user.isActive
        ? `Deactivate ${user.email}? They will keep their login but be denied access to all Guild Insights data.`
        : `Reactivate ${user.email}? They will regain access to Guild Insights.`
    );
    if (!confirmed) return;

    onActionError(null);
    try {
      await updateMutation.mutateAsync({
        userId: user.id,
        isActive: !user.isActive,
      });
    } catch (err) {
      onActionError(
        actionErrorMessage(
          err,
          `Failed to ${user.isActive ? 'deactivate' : 'reactivate'} ${user.email}`
        )
      );
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-100">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            <th className="px-3 py-2">Email</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Role</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Created</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {users.map((u) => (
            <tr key={u.id} className="text-sm">
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5 text-gray-900">
                  <Mail size={12} className="text-gray-400" />
                  <span className="truncate max-w-[18rem]">{u.email}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-gray-700">
                {u.fullName ?? <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-2">
                {/* super_admin rows stay read-only, mirroring Guard A on the
                    endpoint (admin/orgs.ts) so the UI cannot generate a
                    request the API is going to 400. */}
                {u.role === 'super_admin' ? (
                  <RoleChip role={u.role} />
                ) : (
                  <select
                    value={u.role}
                    onChange={(e) =>
                      handleRoleChange(u, e.target.value as AssignableRole)
                    }
                    disabled={isPending}
                    aria-label={`Role for ${u.email}`}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-900 outline-none transition-colors focus:border-guild-green focus:ring-2 focus:ring-guild-green/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="admin">Admin (5)</option>
                    <option value="trainer">Trainer (4)</option>
                    <option value="manager">Manager (3)</option>
                    <option value="operator">Operator (1)</option>
                  </select>
                )}
              </td>
              <td className="px-3 py-2">
                {u.isActive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    <CheckCircle2 size={10} />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    <CircleSlash size={10} />
                    Inactive
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {formatDate(u.createdAt)}
              </td>
              <td className="px-3 py-2 text-right">
                {u.role === 'super_admin' ? (
                  <span
                    className="text-xs text-gray-400"
                    title="super_admin accounts cannot be modified from this surface"
                  >
                    —
                  </span>
                ) : u.isActive ? (
                  <button
                    type="button"
                    onClick={() => handleToggleActive(u)}
                    disabled={isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <UserX size={12} />
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleToggleActive(u)}
                    disabled={isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <UserCheck size={12} />
                    Reactivate
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleChip({ role }: { role: OrgUser['role'] }) {
  const tone =
    role === 'super_admin'
      ? 'bg-purple-50 text-purple-700 ring-purple-200'
      : role === 'admin'
        ? 'bg-blue-50 text-blue-700 ring-blue-200'
        : role === 'trainer'
          ? 'bg-amber-50 text-amber-700 ring-amber-200'
          : role === 'manager'
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            : 'bg-gray-100 text-gray-600 ring-gray-200';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${tone}`}
    >
      {role}
    </span>
  );
}

// ============================================
// TOGGLE CLUSTER CARD — shared shell
// ============================================

interface ToggleClusterCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  isDirty: boolean;
  isSaving: boolean;
  banner: SuccessBanner | null;
  errorMessage: string | null;
  onSave: () => void;
  children?: React.ReactNode;
}

function ToggleClusterCard({
  icon,
  title,
  description,
  enabled,
  onToggle,
  isDirty,
  isSaving,
  banner,
  errorMessage,
  onSave,
  children,
}: ToggleClusterCardProps) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-guild-mint/40 text-guild-green">
            {icon}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="mt-0.5 text-xs text-gray-500 max-w-md">
              {description}
            </p>
          </div>
        </div>

        {/* Switch */}
        <Switch
          checked={enabled}
          onChange={onToggle}
          disabled={isSaving}
          label={`${title} enabled`}
        />
      </div>

      {/* Body — only when enabled, for clusters that have body content */}
      {enabled && children && <div className="px-5 py-4">{children}</div>}

      {/* Footer: banner + save button */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gray-50 border-t border-gray-100 rounded-b-xl">
        <div className="min-w-0 flex-1">
          {errorMessage ? (
            <div className="flex items-start gap-1.5 text-xs text-red-600">
              <AlertCircle size={12} className="mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          ) : banner ? (
            <div
              className={`flex items-center gap-1.5 text-xs ${
                banner.tone === 'success'
                  ? 'text-emerald-700'
                  : 'text-blue-700'
              }`}
            >
              <CheckCircle2 size={12} />
              <span>{banner.message}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-400">
              {isDirty ? 'Unsaved changes' : ''}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || isSaving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-guild-green px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-guild-green/95 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save size={12} />
              Save
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================
// SWITCH — minimal accessible toggle
// ============================================

function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? 'bg-guild-green' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// ============================================
// CLUSTER 1 — TRAINING DISTRIBUTION
// ============================================

interface TrainingDistributionInitial {
  enabled: boolean;
  scopeSectionIds: string[];
}

function TrainingDistributionCluster({
  orgId,
  initial,
}: {
  orgId: string;
  initial: TrainingDistributionInitial;
}) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [scopeSectionIds, setScopeSectionIds] = useState<string[]>(
    initial.scopeSectionIds
  );
  const [banner, setBanner] = useState<SuccessBanner | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-sync local form state when the upstream `initial` snapshot changes
  // (e.g., after the org-detail query refetches with a new server value).
  // Uses a string-stable comparison key to avoid re-syncing on every render.
  const initialKey = useMemo(
    () =>
      JSON.stringify({
        enabled: initial.enabled,
        ids: [...initial.scopeSectionIds].sort(),
      }),
    [initial.enabled, initial.scopeSectionIds]
  );

  useEffect(() => {
    setEnabled(initial.enabled);
    setScopeSectionIds(initial.scopeSectionIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const sectionsLookup = useOrgSectionsLookup(orgId);
  const updateMutation = useUpdateOrgSettings();

  const isDirty = useMemo(() => {
    if (enabled !== initial.enabled) return true;
    const a = [...scopeSectionIds].sort();
    const b = [...initial.scopeSectionIds].sort();
    if (a.length !== b.length) return true;
    return a.join('|') !== b.join('|');
  }, [enabled, initial.enabled, scopeSectionIds, initial.scopeSectionIds]);

  // Auto-dismiss success banner.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const handleSave = async () => {
    setErrorMessage(null);
    setBanner(null);
    try {
      await updateMutation.mutateAsync({
        orgId,
        trainingDistribution: {
          enabled,
          // null clears the allowlist (any section passes); empty array
          // here is sent as []. Treat empty array as "no allowlist" by
          // sending null so the backend writes a clean leaf.
          scopeSectionIds:
            scopeSectionIds.length === 0 ? null : scopeSectionIds,
        },
      });
      setBanner({ tone: 'success', message: 'Saved.' });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to save settings');
      }
    }
  };

  const sectionOptions = useMemo(() => {
    const rows = sectionsLookup.data ?? [];
    return rows.map((s: OrgSectionLookupItem) => ({
      id: s.id,
      name: `${s.sopTitle} → ${s.title}`,
      isActive: true,
    }));
  }, [sectionsLookup.data]);

  return (
    <ToggleClusterCard
      icon={<Layers size={16} />}
      title="Training Distribution"
      description="Concentric Ring 1 + Ring 2 dispatch on low-score assessments. Disable for orgs that haven't approved it yet."
      enabled={enabled}
      onToggle={setEnabled}
      isDirty={isDirty}
      isSaving={updateMutation.isPending}
      banner={banner}
      errorMessage={errorMessage}
      onSave={handleSave}
    >
      <div className="space-y-3">
        <ScopeChipPicker
          label="Section allowlist"
          options={sectionOptions}
          selectedIds={scopeSectionIds}
          onChange={setScopeSectionIds}
          required={false}
          placeholder={
            sectionsLookup.isLoading
              ? 'Loading sections…'
              : sectionOptions.length === 0
                ? 'No sections yet'
                : 'Select sections…'
          }
          helpText="Empty = all sections in scope. Pick to stage a rollout."
          disabled={updateMutation.isPending}
        />
        {sectionsLookup.error && (
          <p className="flex items-center gap-1.5 text-xs text-red-600">
            <AlertCircle size={12} />
            Failed to load sections — try refreshing the page.
          </p>
        )}
      </div>
    </ToggleClusterCard>
  );
}

// ============================================
// CLUSTER 2 — LIVING SOPS
// ============================================
//
// Bug D-2 (toggle audit, May 8, 2026): only `enabled` is read at runtime
// (`apps/api/src/services/living-sops-dispatch.service.ts:144`). Surfacing
// `scopeSectionIds` or `scopeSources` would create silent no-ops.

function LivingSopsCluster({
  orgId,
  initial,
}: {
  orgId: string;
  initial: { enabled: boolean };
}) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [banner, setBanner] = useState<SuccessBanner | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-sync if the underlying detail refetches.
  useEffect(() => {
    setEnabled(initial.enabled);
  }, [initial.enabled]);

  const updateMutation = useUpdateOrgSettings();

  const isDirty = enabled !== initial.enabled;

  // Auto-dismiss success banner.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const handleSave = async () => {
    setErrorMessage(null);
    setBanner(null);
    try {
      await updateMutation.mutateAsync({
        orgId,
        livingSops: { enabled },
      });
      setBanner({ tone: 'success', message: 'Saved.' });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to save settings');
      }
    }
  };

  return (
    <ToggleClusterCard
      icon={<Sparkles size={16} />}
      title="Living SOPs"
      description="AI-proposed SOP revisions inferred from operator performance signals. Disable to pause proposal generation for this org."
      enabled={enabled}
      onToggle={setEnabled}
      isDirty={isDirty}
      isSaving={updateMutation.isPending}
      banner={banner}
      errorMessage={errorMessage}
      onSave={handleSave}
    />
  );
}

// ============================================
// CLUSTER 3 — SUPERVISED MODE (Phase 36.1d)
// ============================================
//
// OPT-IN, unlike the two clusters above. `org.supervisedModeEnabled` is
// resolved server-side as `=== true` to mirror `getOrgSupervisedModeSettings()`
// exactly — "hold every assessment for human review" is not a safe meaning for
// an absent key, and a toggle that read ON for orgs holding nothing would be
// worse than no toggle.
//
// NO `scopeScheduleIds` PICKER, deliberately. The key HAS a runtime reader (so
// this is not the Bug D-2 pattern), but editing it here would need a
// cross-org schedules lookup endpoint that does not exist — this panel is a
// super_admin editing ANOTHER org, and `/api/assessment-schedules` is scoped to
// the caller's own. The value is round-tripped verbatim so the JSONB
// shallow-merge contract cannot silently drop an allowlist set via the API,
// and it stays settable through `PATCH /api/admin/orgs/:orgId/settings`.

function SupervisedModeCluster({
  orgId,
  initial,
}: {
  orgId: string;
  initial: { enabled: boolean; scopeScheduleIds: string[] | null };
}) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [banner, setBanner] = useState<SuccessBanner | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-sync if the underlying detail refetches.
  useEffect(() => {
    setEnabled(initial.enabled);
  }, [initial.enabled]);

  const updateMutation = useUpdateOrgSettings();

  const isDirty = enabled !== initial.enabled;

  // Auto-dismiss success banner.
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const handleSave = async () => {
    setErrorMessage(null);
    setBanner(null);
    try {
      await updateMutation.mutateAsync({
        orgId,
        // FULL leaf cluster — the PATCH shallow-merges per top-level key, so
        // omitting `scopeScheduleIds` would delete it. Empty normalises to
        // null, matching TrainingDistributionCluster.
        supervisedMode: {
          enabled,
          scopeScheduleIds:
            initial.scopeScheduleIds && initial.scopeScheduleIds.length > 0
              ? initial.scopeScheduleIds
              : null,
        },
      });
      setBanner({ tone: 'success', message: 'Saved.' });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorMessage(err.message);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to save settings');
      }
    }
  };

  return (
    <ToggleClusterCard
      icon={<UserCheck size={16} />}
      title="Supervised Mode (learning period)"
      description="Hold scheduled assessments in Job Review so a trainer confirms or corrects the AI's Job before the assessment is created. Confirming is free; correcting re-runs the analysis. This is a temporary learning period, not a permanent setting — assessments will wait for a human while it is on."
      enabled={enabled}
      onToggle={setEnabled}
      isDirty={isDirty}
      isSaving={updateMutation.isPending}
      banner={banner}
      errorMessage={errorMessage}
      onSave={handleSave}
    />
  );
}

// ============================================
// LOADING + ERROR STATES
// ============================================

function PanelLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={20} className="text-guild-green animate-spin" />
      <span className="ml-2 text-sm text-gray-500">Loading organization…</span>
    </div>
  );
}

function PanelError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-red-800">
          Failed to load organization
        </p>
        <p className="text-xs text-red-600">
          Refresh and try again, or contact support.
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors inline-flex items-center gap-1"
      >
        <RefreshCw size={12} />
        Retry
      </button>
    </div>
  );
}

export default OrganizationDetailPanel;
